import { useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { ChevronDown, ChevronUp, Columns3 } from 'lucide-react'

// A small table with sortable and hideable columns.
//
// Built on @tanstack/react-table rather than by hand. ai-site-gen has its own DataTable,
// which is proven but is ~400 lines of another repo's component carrying features this app
// does not have (server pagination, badge thresholds, drilldown); copying it would be
// duplicating a maintenance burden across two codebases to get column visibility. The
// library is headless, so the markup and Tailwind classes below stay this app's own, and it
// is from the same family as the @tanstack/react-query already in use here.

// The library's column helper, re-exported so callers get real inference on accessors.
// A bare ColumnDef<T, unknown> makes every cell's value `unknown`, which pushes casts into
// every renderer.
export function columnHelperFor<T>() {
  return createColumnHelper<T>()
}

export type { ColumnDef }

interface DataTableProps<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the library's own
  // ColumnDef is invariant in its value type, so a list of columns with different accessor
  // types has no single narrower type that accepts them all.
  columns: ColumnDef<T, any>[]
  rows: T[]
  /** Column ids hidden until the reader asks for them. */
  initiallyHidden?: string[]
  /** Shown in place of the table when there is nothing yet. */
  emptyLabel: string
  /** Labels the column picker for screen readers when a page has more than one table. */
  label?: string
}

export function DataTable<T>({
  columns,
  rows,
  initiallyHidden = [],
  emptyLabel,
  label = 'tabel',
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    Object.fromEntries(initiallyHidden.map((id) => [id, false])),
  )
  const [pickerOpen, setPickerOpen] = useState(false)

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const hideableColumns = table.getAllLeafColumns().filter((c) => c.getCanHide())

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <div className="relative">
          <button
            type="button"
            aria-label={`Kolommen kiezen voor ${label}`}
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((open) => !open)}
            className="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs text-ink-soft hover:border-amber hover:text-amber"
          >
            <Columns3 size={14} />
            Kolommen
          </button>
          {pickerOpen && (
            <div className="absolute right-0 z-10 mt-1 min-w-44 rounded-lg border border-line bg-card p-2 shadow-lg">
              {hideableColumns.map((column) => (
                <label
                  key={column.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-line/40"
                >
                  <input
                    type="checkbox"
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                  />
                  {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="text-left text-xs uppercase tracking-wide text-ink-soft">
              {headerGroup.headers.map((header) => {
                const sortable = header.column.getCanSort()
                const sorted = header.column.getIsSorted()
                return (
                  <th key={header.id} className="border-b border-line pb-2">
                    {sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="flex items-center gap-1 uppercase tracking-wide hover:text-amber"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' && <ChevronUp size={12} />}
                        {sorted === 'desc' && <ChevronDown size={12} />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={table.getVisibleLeafColumns().length} className="py-2 text-ink-soft">
                {emptyLabel}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="border-b border-line py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
